import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';

export interface DashboardColorSettings {
  backgroundColor: string;
  widgetBackground: string;
  widgetBorder: string;
  textPrimary: string;
  textSecondary: string;
  widgetSpacing: number;
  showGridlines: boolean;
  gridlineColor: string;
}

export interface DashboardSettings {
  [dashboardId: string]: DashboardColorSettings;
}

interface DashboardSettingsContextType {
  settings: DashboardSettings;
  getCurrentSettings: (dashboardId: string) => DashboardColorSettings;
  updateSettings: (dashboardId: string, newSettings: Partial<DashboardColorSettings>) => void;
  resetSettings: (dashboardId: string) => void;
  applySettingsToDOM: (settings: DashboardColorSettings) => void;
}

// Default color settings
const DEFAULT_SETTINGS: DashboardColorSettings = {
  backgroundColor: '#f9fafb',
  widgetBackground: '#ffffff',
  widgetBorder: '#e5e7eb',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
  widgetSpacing: 16,
  showGridlines: false,
  gridlineColor: '#e5e7eb'
};

// Dark mode defaults
const DARK_DEFAULT_SETTINGS: DashboardColorSettings = {
  backgroundColor: '#111827',
  widgetBackground: '#1f2937',
  widgetBorder: '#374151',
  textPrimary: '#f9fafb',
  textSecondary: '#d1d5db',
  widgetSpacing: 16,
  showGridlines: false,
  gridlineColor: '#374151'
};

const STORAGE_KEY = 'dashboard-color-settings';

const DashboardSettingsContext = createContext<DashboardSettingsContextType | undefined>(undefined);

export const useDashboardSettings = () => {
  const context = useContext(DashboardSettingsContext);
  if (context === undefined) {
    throw new Error('useDashboardSettings must be used within a DashboardSettingsProvider');
  }
  return context;
};

interface DashboardSettingsProviderProps {
  children: ReactNode;
}

export const DashboardSettingsProvider = ({ children }: DashboardSettingsProviderProps) => {
  const [settings, setSettings] = useState<DashboardSettings>({});

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setSettings(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load dashboard settings:', error);
    }
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save dashboard settings:', error);
    }
  }, [settings]);

  const getCurrentSettings = (dashboardId: string): DashboardColorSettings => {
    if (settings[dashboardId]) {
      return settings[dashboardId];
    }
    
    // Return default based on current theme
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDarkMode ? DARK_DEFAULT_SETTINGS : DEFAULT_SETTINGS;
  };

  const updateSettings = (dashboardId: string, newSettings: Partial<DashboardColorSettings>) => {
    const currentSettings = getCurrentSettings(dashboardId);
    const updatedSettings = { ...currentSettings, ...newSettings };
    
    setSettings(prev => ({
      ...prev,
      [dashboardId]: updatedSettings
    }));

    // Apply settings immediately to CSS custom properties
    applyCSSSettings(updatedSettings);
  };

  const resetSettings = (dashboardId: string) => {
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const defaultSettings = isDarkMode ? DARK_DEFAULT_SETTINGS : DEFAULT_SETTINGS;
    
    setSettings(prev => ({
      ...prev,
      [dashboardId]: defaultSettings
    }));

    applyCSSSettings(defaultSettings);
  };

  const getContrastColor = (backgroundColor: string): string => {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black or white based on luminance
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  const applyCSSSettings = (colorSettings: DashboardColorSettings) => {
    const root = document.documentElement;
    root.style.setProperty('--dashboard-bg-color', colorSettings.backgroundColor);
    root.style.setProperty('--dashboard-widget-bg', colorSettings.widgetBackground);
    root.style.setProperty('--dashboard-widget-border', colorSettings.widgetBorder);

    // Auto-calculate text colors based on background
    const primaryTextColor = getContrastColor(colorSettings.widgetBackground);
    const secondaryTextColor = primaryTextColor === '#000000' ? '#6b7280' : '#d1d5db';

    root.style.setProperty('--dashboard-text-primary', primaryTextColor);
    root.style.setProperty('--dashboard-text-secondary', secondaryTextColor);
    root.style.setProperty('--dashboard-widget-spacing', `${colorSettings.widgetSpacing}px`);
    root.style.setProperty('--dashboard-gridline-color', colorSettings.gridlineColor);
    root.style.setProperty('--dashboard-show-gridlines', colorSettings.showGridlines ? '1' : '0');
  };


  return (
    <DashboardSettingsContext.Provider value={{
      settings,
      getCurrentSettings,
      updateSettings,
      resetSettings,
      applySettingsToDOM: applyCSSSettings
    }}>
      {children}
    </DashboardSettingsContext.Provider>
  );
};